// Helpers to position reservation blocks on the week calendar grid.

import type { Stylist } from '@app-types/models/stylist';
import type { Business } from '@app-types/models/business';

// Parse an "HH:MM" string into minutes-since-midnight. Falls back to `def`.
export const hhmmToMinutes = (hhmm?: string, def = 0): number => {
  if (!hhmm || typeof hhmm !== 'string' || !hhmm.includes(':')) return def;
  const [h, m] = hhmm.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return def;
  return h * 60 + m;
};

// Minutes-since-midnight for a Date (local time).
export const dateToMinutes = (date: string | Date): number => {
  const d = new Date(date);
  return d.getHours() * 60 + d.getMinutes();
};

// Resolve the visible day window (in minutes) from stylist availability or business hours.
// Defaults to 10:00–20:00 if nothing is configured.
export const resolveDayWindow = (stylist?: Stylist | null, business?: Business | null): { startMin: number; endMin: number } => {
  const start = stylist?.availability?.startHour || business?.businessHours?.startHour || '10:00';
  const end = stylist?.availability?.endHour || business?.businessHours?.endHour || '20:00';
  return {
    startMin: hhmmToMinutes(start, 600),
    endMin: hhmmToMinutes(end, 1200),
  };
};

// Given a reservation and the day window, return { top, height } in px for HOUR_PX row height,
// clamped to the visible window.
export const blockGeometry = (
  reservation: { start: string | Date; end: string | Date },
  startMin: number,
  endMin: number,
  hourPx: number,
): { top: number; height: number } => {
  const s = Math.max(dateToMinutes(reservation.start), startMin);
  const e = Math.min(dateToMinutes(reservation.end), endMin);
  const top = ((s - startMin) / 60) * hourPx;
  const height = Math.max(((e - s) / 60) * hourPx, 22);
  return { top, height };
};

// Build the array of hour labels (e.g. ["10:00", "11:00", ...]) for the time gutter.
export const hourLabels = (startMin: number, endMin: number): string[] => {
  const labels: string[] = [];
  for (let m = startMin; m < endMin; m += 60) {
    const h = Math.floor(m / 60);
    labels.push(`${String(h).padStart(2, '0')}:00`);
  }
  return labels;
};

// Convert minutes-since-midnight to an "HH:MM" string.
export const minutesToHHMM = (min: number): string => {
  const clamped = Math.max(0, Math.round(min));
  const h = Math.floor(clamped / 60);
  const m = clamped % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

// Enumerate selectable start-time options ("HH:MM") across a day window at `step`-minute granularity.
export const slotOptions = (startMin: number, endMin: number, step = 15): string[] => {
  const out: string[] = [];
  for (let m = startMin; m < endMin; m += step) out.push(minutesToHHMM(m));
  return out;
};
